import { FormEvent, memo } from "react";

import { createCategory, editCategory } from "@/api/category";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CategoryType } from "@/types/category";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  category: CategoryType | null;
  isOpen: boolean;
  onClose: () => void;
}

const CategoryModal: React.FC<Props> = ({ isOpen, onClose, category }) => {
  const title = category ? `ویرایش دسته بندی ${category.name}` : "ساخت دسته بندی جدید";
  const description = category ? "ویرایش دسته بندی" : "ساخت دسته بندی جدید برای سیستم";

  const onChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Content category={category} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};

const Content = memo(
  ({ category, onClose }: { category: CategoryType | null; onClose: () => void }) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
      mutationFn: (data: any) => (category ? editCategory(data) : createCategory(data)),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["categories"] });
        onClose();
      }
    });

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const formData = new FormData(e.currentTarget);
      const data: Record<string, any> = Object.fromEntries(formData);

      if (category) {
        data.id = category.id;
      }

      mutate(data);
    };

    return (
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-y-1">
          <label htmlFor="" className="text-sm">
            نام
          </label>
          <Input
            type="text"
            name="name"
            placeholder="نام دسته بندی"
            defaultValue={category?.name ?? ""}
            required
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <label htmlFor="" className="text-sm">
            توضیحات
          </label>
          <Textarea
            name="description"
            placeholder="توضیحات..."
            className="resize-none"
            defaultValue={category?.description ?? ""}
          />
        </div>
        <Button type="submit" className="w-full" disabled={isPending}>
          ذخیره
        </Button>
      </form>
    );
  }
);

export default CategoryModal;
